import React, { useEffect } from "react";
import { FileText, ShieldCheck, Trash2 } from "lucide-react";
import { legalPages } from "../mock";

const LegalGroup = ({ group }) => (
  <div className="rounded-2xl border border-white/60 bg-white/45 p-5 shadow-[0_16px_45px_rgba(22,38,63,0.06)] backdrop-blur-xl">
    <h3 className="text-[18px] font-bold text-[#16263F]">{group.title}</h3>
    {group.text && (
      <p className="mt-3 text-[15px] leading-7 text-[#405574]">{group.text}</p>
    )}
    {group.list && (
      <ul className="mt-4 grid gap-2 text-[15px] leading-7 text-[#405574]">
        {group.list.map((item) => (
          <li key={item} className="flex gap-3">
            <span className="mt-3 h-1.5 w-1.5 shrink-0 rounded-full bg-[#5BA4FB]" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const LegalSection = ({ section }) => (
  <section className="border-t border-[#C8D8EC]/70 pt-8 first:border-t-0 first:pt-0">
    <h2 className="text-[23px] font-bold tracking-tight text-[#16263F]">
      {section.title}
    </h2>

    {section.content && (
      <div className="mt-4 space-y-2 text-[15px] leading-7 text-[#405574]">
        {section.content.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>
    )}

    {section.notes && (
      <div className="mt-5 space-y-3 rounded-2xl border border-[#BBD7F7] bg-[#EAF4FF]/70 p-5 text-[14px] leading-7 text-[#315A8A]">
        {section.notes.map((note) => (
          <p key={note}>{note}</p>
        ))}
      </div>
    )}

    {section.list && (
      <ul className="mt-4 grid gap-2 text-[15px] leading-7 text-[#405574]">
        {section.list.map((item) => (
          <li key={item} className="flex gap-3">
            <span className="mt-3 h-1.5 w-1.5 shrink-0 rounded-full bg-[#5BA4FB]" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    )}

    {section.groups && (
      <div className="mt-5 grid gap-4">
        {section.groups.map((group) => (
          <LegalGroup key={group.title} group={group} />
        ))}
      </div>
    )}
  </section>
);

const LegalPage = ({ type }) => {
  const page = legalPages[type] || legalPages.impressum;
  const Icon =
    type === "datenschutz"
      ? ShieldCheck
      : type === "deletion"
        ? Trash2
        : FileText;

  useEffect(() => {
    document.title = `${page.title} | OpenPenguin Software`;
  }, [page.title]);

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#EDF1F8] px-6 pb-16 pt-28 text-[#16263F] md:pt-36">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(91,164,251,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(91,164,251,0.08)_1px,transparent_1px)] bg-[size:56px_56px]" />
        <div className="absolute -right-20 top-14 h-[360px] w-[360px] rounded-full bg-[#5BA4FB]/20 blur-3xl" />
        <div className="absolute -bottom-24 left-10 h-[320px] w-[320px] rounded-full bg-white/70 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-[880px]">
        <header>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/50 px-4 py-2 shadow-[0_12px_35px_rgba(22,38,63,0.06)] backdrop-blur-xl">
            <Icon className="h-4 w-4 text-[#5BA4FB]" />
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#5A6E8D]">
              {page.eyebrow}
            </span>
          </div>
          <h1 className="mt-6 text-[44px] font-black leading-[0.98] tracking-tight text-[#16263F] md:text-[64px]">
            {page.title}
          </h1>
          <p className="mt-5 text-sm font-semibold text-[#5BA4FB]">{page.updated}</p>
          <p className="mt-4 max-w-2xl text-[17px] leading-8 text-[#405574]">
            {page.intro}
          </p>
        </header>

        <article className="mt-10 rounded-[32px] border border-white/75 bg-white/55 p-6 shadow-[0_30px_90px_rgba(22,38,63,0.09)] backdrop-blur-2xl md:p-10 lg:p-12">
          <div className="grid gap-9">
            {page.sections.map((section) => (
              <LegalSection key={section.title} section={section} />
            ))}
          </div>
        </article>
      </div>
    </main>
  );
};

export default LegalPage;
